import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FileText, Database, Download, Loader2 } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import api from '@/lib/api';

interface DisparoAvulsoLite {
  id: number;
  template_name?: string;
  status?: string;
  total_contatos?: number;
  enviados?: number;
  falhas?: number;
  client_id?: number;
  created_at?: string;
  whatsappConnection?: { name?: string };
}

interface ContatoLite {
  id: number;
  telefone_destino: string;
  variaveis?: string[];
  status_envio: string;
  enviado_at?: string | null;
  motivo_rejeicao?: string | null;
}

interface ExportarDisparoAvulsoModalProps {
  isOpen: boolean;
  onClose: () => void;
  disparo: DisparoAvulsoLite | null;
  clientId?: number | null;
}

const STATUS_OPTIONS = [
  { value: 'all', label: 'Todos os Status' },
  { value: 'PENDENTE', label: 'Pendente' },
  { value: 'FILA', label: 'Fila' },
  { value: 'ENVIADO', label: 'Enviado' },
  { value: 'FALHA', label: 'Falha' },
  { value: 'CONTATO_INVALIDO', label: 'Contato Inválido' },
];

// Limite alto para puxar todos os contatos numa única chamada (alinhado ao
// comportamento usado no export de Lotes, que também busca tudo de uma vez).
const FETCH_LIMIT = 10000;

const ExportarDisparoAvulsoModal: React.FC<ExportarDisparoAvulsoModalProps> = ({
  isOpen,
  onClose,
  disparo,
  clientId,
}) => {
  const [exportFormat, setExportFormat] = useState<'pdf' | 'xlsx'>('pdf');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setExportFormat('pdf');
      setStatusFilter('all');
      setIsExporting(false);
    }
  }, [isOpen]);

  if (!disparo) return null;

  const fetchContatos = async (): Promise<ContatoLite[]> => {
    const cid = clientId ?? disparo.client_id ?? undefined;
    const res = await api.disparoAvulsoGetContatos(disparo.id, 1, FETCH_LIMIT, cid);
    return ((res?.contatos as ContatoLite[]) || []);
  };

  const filterByStatus = (contatos: ContatoLite[]) =>
    statusFilter === 'all'
      ? contatos
      : contatos.filter((c) => c.status_envio === statusFilter);

  // Calcula a quantidade máxima de variáveis presente em qualquer contato,
  // para gerar colunas Var1..VarN dinamicamente no export.
  const maxVarsCount = (contatos: ContatoLite[]) =>
    contatos.reduce((acc, c) => Math.max(acc, Array.isArray(c.variaveis) ? c.variaveis.length : 0), 0);

  const exportPDF = (contatos: ContatoLite[]) => {
    const doc = new jsPDF('l', 'mm', 'a4');
    let y = 20;

    doc.setFontSize(16);
    doc.text(`Disparo Avulso #${disparo.id}`, 14, y);
    y += 8;

    doc.setFontSize(10);
    doc.text(`Template: ${disparo.template_name || '-'}`, 14, y); y += 5;
    doc.text(`Conexão: ${disparo.whatsappConnection?.name || '-'}`, 14, y); y += 5;
    doc.text(`Status: ${disparo.status || '-'}`, 14, y); y += 5;
    doc.text(
      `Total: ${disparo.total_contatos ?? '-'} | Enviados: ${disparo.enviados ?? '-'} | Falhas: ${disparo.falhas ?? '-'}`,
      14, y
    ); y += 5;
    doc.text(
      `Filtro de Status: ${statusFilter === 'all' ? 'Todos' : STATUS_OPTIONS.find((s) => s.value === statusFilter)?.label}`,
      14, y
    ); y += 5;
    doc.text(`Contatos exportados: ${contatos.length}`, 14, y); y += 8;

    const varsCount = maxVarsCount(contatos);
    const headers = [
      'ID',
      'Telefone',
      'Status',
      'Enviado em',
      ...Array.from({ length: varsCount }, (_, i) => `Var${i + 1}`),
      'Motivo Rejeição',
    ];
    const body = contatos.map((c) => [
      c.id,
      c.telefone_destino,
      c.status_envio,
      c.enviado_at ? new Date(c.enviado_at).toLocaleString('pt-BR') : '-',
      ...Array.from({ length: varsCount }, (_, i) => (c.variaveis?.[i] ?? '')),
      c.motivo_rejeicao || '-',
    ]);

    autoTable(doc, {
      head: [headers],
      body,
      startY: y,
      styles: { fontSize: 7 },
      headStyles: { fillColor: [66, 139, 202] },
      alternateRowStyles: { fillColor: [245, 245, 245] },
    });

    doc.save(`disparo-avulso-${disparo.id}-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const exportXLSX = (contatos: ContatoLite[]) => {
    const varsCount = maxVarsCount(contatos);
    const rows = contatos.map((c) => {
      const base: Record<string, unknown> = {
        'ID': c.id,
        'Telefone': c.telefone_destino,
        'Status': c.status_envio,
        'Enviado em': c.enviado_at ? new Date(c.enviado_at).toLocaleString('pt-BR') : '-',
      };
      for (let i = 0; i < varsCount; i++) {
        base[`Var${i + 1}`] = c.variaveis?.[i] ?? '';
      }
      base['Motivo Rejeição'] = c.motivo_rejeicao || '';
      return base;
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);
    // Sheet "Resumo" com metadados do disparo
    const resumoWs = XLSX.utils.json_to_sheet([
      { Campo: 'Disparo ID', Valor: disparo.id },
      { Campo: 'Template', Valor: disparo.template_name || '-' },
      { Campo: 'Conexão', Valor: disparo.whatsappConnection?.name || '-' },
      { Campo: 'Status', Valor: disparo.status || '-' },
      { Campo: 'Total', Valor: disparo.total_contatos ?? '-' },
      { Campo: 'Enviados', Valor: disparo.enviados ?? '-' },
      { Campo: 'Falhas', Valor: disparo.falhas ?? '-' },
      { Campo: 'Filtro Status', Valor: statusFilter === 'all' ? 'Todos' : statusFilter },
      { Campo: 'Contatos exportados', Valor: contatos.length },
    ]);
    XLSX.utils.book_append_sheet(wb, resumoWs, 'Resumo');
    XLSX.utils.book_append_sheet(wb, ws, 'Contatos');
    XLSX.writeFile(wb, `disparo-avulso-${disparo.id}-${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const contatos = await fetchContatos();
      const filtered = filterByStatus(contatos);
      if (filtered.length === 0) {
        toast({
          title: 'Aviso',
          description: 'Nenhum contato encontrado com o filtro aplicado.',
          variant: 'destructive',
        });
        return;
      }
      if (exportFormat === 'pdf') exportPDF(filtered);
      else exportXLSX(filtered);
      toast({ title: 'Sucesso', description: `Exportação concluída (${filtered.length} contatos).` });
      onClose();
    } catch (e: any) {
      console.error('[ExportarDisparoAvulso] erro:', e);
      toast({
        title: 'Erro',
        description: e?.message || 'Erro ao exportar disparo.',
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Exportar Disparo #{disparo.id}</DialogTitle>
          <DialogDescription>
            Selecione o formato e o filtro de status para o download.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <div className="space-y-3">
            <Label className="text-sm font-medium">Formato</Label>
            <div className="grid grid-cols-2 gap-3">
              <Button
                variant={exportFormat === 'pdf' ? 'default' : 'outline'}
                onClick={() => setExportFormat('pdf')}
                className="flex items-center gap-2"
                disabled={isExporting}
              >
                <FileText className="h-4 w-4" />
                PDF
              </Button>
              <Button
                variant={exportFormat === 'xlsx' ? 'default' : 'outline'}
                onClick={() => setExportFormat('xlsx')}
                className="flex items-center gap-2"
                disabled={isExporting}
              >
                <Database className="h-4 w-4" />
                Excel
              </Button>
            </div>
          </div>

          <div className="space-y-3">
            <Label className="text-sm font-medium">Filtrar por Status</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter} disabled={isExporting}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="bg-gray-50 p-3 rounded-lg text-sm text-gray-600 space-y-1">
            <p><strong>Disparo:</strong> #{disparo.id} – {disparo.template_name || '-'}</p>
            <p><strong>Status:</strong> {disparo.status || '-'}</p>
            <p><strong>Total / Enviados / Falhas:</strong> {disparo.total_contatos ?? 0} / {disparo.enviados ?? 0} / {disparo.falhas ?? 0}</p>
          </div>

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={onClose} disabled={isExporting}>
              Cancelar
            </Button>
            <Button onClick={handleExport} disabled={isExporting} className="flex items-center gap-2">
              {isExporting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Exportando...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4" />
                  Exportar
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ExportarDisparoAvulsoModal;
